import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import ModalContainer from '@/components/modals/ModalContainer';
import { toastError } from '@/components/Toast';
import {
	useGetSopByFilterIdQuery,
	useGetSopByIdQuery
} from '@/features/task-configuration/api/taskConfigurationApi';
import { SOP_LINK_TYPES } from '@/features/task-configuration/constants';
import { downloadSingleFile } from '@/services/assetsService';
import { downloadBlobFIle, isAdmin, parseHtml } from '@/utils/Util';

import CreateAndEditSop from './CreateAndEditSop';

const SopViewModal = ({ selectedSopId, onClose, isFilterId = false }) => {
	const { data: sopDataById } = useGetSopByIdQuery(
		{ id: selectedSopId },
		{ skip: !selectedSopId || isFilterId }
	);
	const { data: sopDataByFilterId } = useGetSopByFilterIdQuery(
		{ filterId: selectedSopId },
		{ skip: !selectedSopId || !isFilterId }
	);
	const sopData = isFilterId ? sopDataByFilterId : sopDataById;
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);

	const getLinkTypeLabel = (linkType) => {
		return SOP_LINK_TYPES.find((t) => t.id === linkType)?.value || linkType;
	};

	const getRichTextValue = (value) => {
		if (!value) return '-';
		const trimmed = value.trim();
		if (!trimmed || trimmed === '<p></p>') return '-';
		return trimmed;
	};

	const { sopLinks, metabaseLinks, tutorialLinks, otherLinks, documentAttachments } = useMemo(() => {
		const attachments = sopData?.attachments || [];
		const hyperlinks = attachments.filter((att) => att.type === 'HYPERLINK');
		return {
			sopLinks: hyperlinks.filter((att) => att.linkType === 'SOP_LINK'),
			metabaseLinks: hyperlinks.filter((att) => att.linkType === 'METABASE_REPORT'),
			tutorialLinks: hyperlinks.filter((att) => att.linkType === 'TUTORIALS'),
			otherLinks: hyperlinks.filter(
				(att) => !['SOP_LINK', 'METABASE_REPORT', 'TUTORIALS'].includes(att.linkType)
			),
			documentAttachments: attachments.filter((att) => att.type === 'DOCUMENT')
		};
	}, [sopData]);

	const ensureUrl = (url) => {
		if (!url) return '#';
		return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
	};

	const handleFileDownload = async (documentId) => {
		try {
			const response = await downloadSingleFile(documentId);
			downloadBlobFIle(response);
		} catch {
			toastError('Failed to download file');
		}
	};

	return (
		<>
			<ModalContainer className="sop-view-modal" onCloseModal={onClose}>
				<div className="popup-wrapper sop-view-modal">
					<div className="common-popup sop-view-modal body-scroll">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">Journey SOP & tutorials</h3>
							</div>
							<div className="close-btn" onClick={onClose}>
								<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
							</div>
						</div>
						<div className="common-popup-body pb-0 scroll-y-label">
							<div className="sop-content-section">
								<div className="single-sop-card">
									<h4 className="semibold-14 mb-1">SOP title:</h4>
									<p className="regular-14 mb-3">{sopData?.title || '-'}</p>
								</div>
								{/* <div className="single-sop-card">
									<h4 className="semibold-14 mb-1">Stage name:</h4>
									<p className="regular-14 mb-3">{sopData?.stage?.name || '-'}</p>
								</div> */}
								<div className="single-sop-card">
									<h4 className="semibold-14 mb-1">Which styles are you seeing here:</h4>
									<p
										className="regular-14 mb-3"
										dangerouslySetInnerHTML={{
											__html: parseHtml(getRichTextValue(sopData?.description))
										}}
									/>
								</div>
								<div className="single-sop-card">
									<h4 className="semibold-14 mb-1">Summary SOP:</h4>
									<p
										className="regular-14 mb-3"
										dangerouslySetInnerHTML={{
											__html: parseHtml(getRichTextValue(sopData?.sop))
										}}
									/>
								</div>
								{sopLinks.length > 0 && (
									<div className="single-sop-card">
										<h4 className="semibold-14 mb-1">Details SOP:</h4>
										<ul className="sop-list mb-3">
											{sopLinks.map((link) => (
												<li
													key={link.id}
													className="regular-14 blue cursor-pointer ml-3"
												>
													<Link
														to={ensureUrl(link.url)}
														target="_blank"
														rel="noopener noreferrer"
													>
														{link.title || getLinkTypeLabel(link.linkType)}
													</Link>
												</li>
											))}
										</ul>
									</div>
								)}
								{metabaseLinks.length > 0 && (
									<div className="single-sop-card">
										<h4 className="semibold-14 mb-1">Metabase report link:</h4>
										<ul className="sop-list mb-3">
											{metabaseLinks.map((link) => (
												<li
													key={link.id}
													className="regular-14 blue cursor-pointer ml-3"
												>
													<Link
														to={ensureUrl(link.url)}
														target="_blank"
														rel="noopener noreferrer"
													>
														{link.title || 'Click here to view the report'}
													</Link>
												</li>
											))}
										</ul>
									</div>
								)}
								{tutorialLinks.length > 0 && (
									<div className="single-sop-card">
										<h4 className="semibold-14 mb-1">Tutorial link:</h4>
										<ul className="sop-list mb-3">
											{tutorialLinks.map((link) => (
												<li
													key={link.id}
													className="regular-14 blue cursor-pointer ml-3"
												>
													<Link
														to={ensureUrl(link.url)}
														target="_blank"
														rel="noopener noreferrer"
													>
														{link.title || link.url}
													</Link>
												</li>
											))}
										</ul>
									</div>
								)}
								{otherLinks.length > 0 && (
									<div className="single-sop-card">
										<h4 className="semibold-14 mb-1">Other links:</h4>
										<ul className="sop-list mb-3">
											{otherLinks.map((link) => (
												<li
													key={link.id}
													className="regular-14 blue cursor-pointer ml-3"
												>
													<Link
														to={ensureUrl(link.url)}
														target="_blank"
														rel="noopener noreferrer"
													>
														{link.title || getLinkTypeLabel(link.linkType)}
													</Link>
												</li>
											))}
										</ul>
									</div>
								)}
								{documentAttachments.length > 0 && (
									<div className="single-sop-card">
										<h4 className="semibold-14 mb-1">Files:</h4>
										<div className="file-list d-flex gap-2">
											{documentAttachments.map((doc) => (
												<span
													key={doc.id}
													className="regular-14 gray_dark_02 cursor-pointer text-underline"
													onClick={() => handleFileDownload(doc.documentId)}
												>
													{doc.name || `Document ${doc.documentId}`}
												</span>
											))}
										</div>
									</div>
								)}
							</div>
						</div>
						{isAdmin() && sopData && Object.keys(sopData).length > 0 && (
							<div className="modal-footer d-block ">
								<div className="footer-buttons text-left">
									<ul className="sop-list mb-0">
										<li
											className="regular-14 blue cursor-pointer text-underline"
											onClick={() => setIsEditModalOpen(true)}
										>
											<p>Edit SOP</p>
										</li>
									</ul>
								</div>
							</div>
						)}
					</div>
				</div>
			</ModalContainer>
			{isEditModalOpen && (
				<CreateAndEditSop
					sopId={selectedSopId}
					onClose={() => {
						setIsEditModalOpen(false);
						onClose();
					}}
				/>
			)}
		</>
	);
};

export default SopViewModal;
